import { Bell, Gear } from '@phosphor-icons/react';
import type { NotificationDto } from '@reelplicate/api-client/model';
import {
  getListNotificationsQueryKey,
  listNotifications,
  useGetNotificationUnreadCount,
} from '@reelplicate/api-client/notifications';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  Container,
  EmptyState,
  Inline,
  Link,
  NotificationGroup,
  NotificationItem,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@reelplicate/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { toEntry } from './to-entry';
import { useNotificationActions } from './use-notification-actions';

type Filter = 'all' | 'mentions';

const PAGE_SIZE = 20;

/**
 * `/notifications` (Figma 459:4411) - every notification, newest first, with
 * All / Mentions and "Load older". Reached from the popover's "View all
 * notifications". Opening one marks it read and goes to what it's about.
 */
export function AllNotificationsPage() {
  const navigate = useNavigate();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const enabled = Boolean(workspaceId);
  const [filter, setFilter] = useState<Filter>('all');
  const { data: count } = useGetNotificationUnreadCount(workspaceId, {
    query: { enabled, throwOnError: false },
  });
  const feed = useInfiniteQuery({
    queryKey: [...getListNotificationsQueryKey(workspaceId, { filter }), 'infinite'],
    queryFn: ({ pageParam, signal }) =>
      listNotifications(
        workspaceId,
        { filter, limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
  const { markOne, markAll } = useNotificationActions(workspaceId);
  const items = feed.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = filter === 'mentions' ? count?.mentions : count?.all;

  const open = (notification: NotificationDto, path: string) => {
    markOne(notification);
    void navigate(path);
  };

  const panel = (value: Filter) => {
    if (value !== filter) return <TabPanel value={value} />;
    const now = new Date();
    const groups: { label: string; items: NotificationDto[] }[] = [];
    for (const item of items) {
      const label = toEntry(item, now).group ?? '';
      const group = groups.find((g) => g.label === label);
      if (group) group.items.push(item);
      else groups.push({ label, items: [item] });
    }
    return (
      <TabPanel value={value}>
        {!feed.data ? (
          <Skeleton className="h-60" shape="rectangle" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="You’re all caught up"
            description={
              filter === 'mentions'
                ? 'When someone @mentions you or replies to your comment, it shows up here.'
                : 'Mentions, drafts to review and team changes will show up here.'
            }
          />
        ) : (
          <div className="bg-bg-surface border-border-subtle overflow-hidden rounded-xl border pt-1">
            {groups.map((group) => (
              <NotificationGroup key={group.label} label={group.label}>
                {group.items.map((notification) => {
                  const {
                    id: _id,
                    group: _group,
                    mention: _mention,
                    ...entry
                  } = toEntry(notification, now);
                  return (
                    <li key={notification.id}>
                      <NotificationItem
                        {...entry}
                        action={
                          notification.action && (
                            <Link asChild size="sm">
                              <RouterLink
                                to={notification.action.path}
                                onClick={() => markOne(notification)}
                              >
                                {notification.action.label}
                              </RouterLink>
                            </Link>
                          )
                        }
                        onSelect={() => open(notification, notification.targetPath)}
                      />
                    </li>
                  );
                })}
              </NotificationGroup>
            ))}
            {feed.hasNextPage && (
              <Inline align="center" className="pt-4 pb-5">
                <Button
                  variant="outline"
                  loading={feed.isFetchingNextPage}
                  onClick={() => void feed.fetchNextPage()}
                >
                  Load older
                </Button>
              </Inline>
            )}
          </div>
        )}
      </TabPanel>
    );
  };

  return (
    <Container className="pt-8 pb-16">
      <Stack gap={5} className="w-full max-w-200">
        <Inline as="header" gap={3} alignY="center" wrap>
          <Stack gap={1} className="min-w-0 flex-1">
            <h1 className="text-heading-h1 text-text-primary">Notifications</h1>
            <p className="text-body-md text-text-secondary">
              Everything that needed you in {workspaces?.[0]?.name ?? 'your workspace'},
              newest first.
            </p>
          </Stack>
          <Link asChild size="md" disabled={!unread}>
            <button
              type="button"
              disabled={!unread}
              onClick={() => {
                const newest = items[0]?.occurredAt;
                if (newest)
                  markAll(newest, filter === 'mentions' ? 'mentions' : undefined);
              }}
            >
              Mark all as read
            </button>
          </Link>
          <Button asChild size="sm" variant="outline" leadingIcon={Gear}>
            <RouterLink to="/settings/notifications">Settings</RouterLink>
          </Button>
        </Inline>
        <Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
          <TabList aria-label="Notifications">
            <Tab value="all" count={count?.all || undefined}>
              All
            </Tab>
            <Tab value="mentions" count={count?.mentions || undefined}>
              Mentions
            </Tab>
          </TabList>
          <div className="pt-5">
            {panel('all')}
            {panel('mentions')}
          </div>
        </Tabs>
        <p className="text-caption text-text-tertiary">
          Notifications are kept for 90 days after you read them.
        </p>
      </Stack>
    </Container>
  );
}
